import React, { useMemo } from 'react';
import { StarIcon } from '../icons';
import { formatDateSafe } from '../../utils/helpers';
import { BASE_DIMENSIONS } from '../../config/questionnaire';

const CompanyRewards = ({ stars, submissions = [] }) => {
  const level = stars < 5 ? 'Participante activo' : stars < 10 ? 'Embajador sectorial' : 'Referente del panel';
  const orderedSubmissions = useMemo(
    () =>
      [...submissions].sort((a, b) => {
        const dateA = new Date(a.submittedAt || a.resubmittedAt || 0);
        const dateB = new Date(b.submittedAt || b.resubmittedAt || 0);
        return dateB - dateA;
      }),
    [submissions],
  );
  const lastSubmission = orderedSubmissions[0];
  const responseDigest = useMemo(() => {
    if (!orderedSubmissions.length) return null;
    const questionDimensionMap = {};
    const questionTextMap = {};
    BASE_DIMENSIONS.forEach((dimension) => {
      dimension.questions.forEach((question) => {
        if (question.type === 'likert') {
          questionDimensionMap[question.id] = dimension.name;
          questionTextMap[question.id] = question.text;
        }
      });
    });
    const dimensionTotals = {};
    const questionTotals = {};
    let favorable = 0;
    let totalLikerts = 0;
    orderedSubmissions.forEach((submission) => {
      Object.entries(submission.data || {}).forEach(([questionId, rawValue]) => {
        const dimensionName = questionDimensionMap[questionId];
        const numericValue = Number(rawValue);
        if (!dimensionName || !Number.isFinite(numericValue)) return;
        totalLikerts += 1;
        if (numericValue >= 4) favorable += 1;
        if (!dimensionTotals[dimensionName]) {
          dimensionTotals[dimensionName] = { sum: 0, count: 0 };
        }
        dimensionTotals[dimensionName].sum += numericValue;
        dimensionTotals[dimensionName].count += 1;
        if (!questionTotals[questionId]) {
          questionTotals[questionId] = { sum: 0, count: 0, text: questionTextMap[questionId], dimension: dimensionName };
        }
        questionTotals[questionId].sum += numericValue;
        questionTotals[questionId].count += 1;
      });
    });
    if (!totalLikerts) return null;
    const dimensionAverages = Object.entries(dimensionTotals)
      .map(([name, stats]) => ({ name, score: stats.sum / stats.count }))
      .sort((a, b) => b.score - a.score);
    const weakQuestions = Object.values(questionTotals)
      .map((entry) => ({ ...entry, score: entry.sum / entry.count }))
      .sort((a, b) => a.score - b.score)
      .slice(0, 3);
    return {
      dimensionAverages,
      weakQuestions,
      favorableRatio: favorable / totalLikerts,
      totalLikerts,
    };
  }, [orderedSubmissions]);

  const summaryCards = [
    { label: 'Participaciones totales', value: submissions.length },
    { label: 'Último envío', value: lastSubmission ? formatDateSafe(lastSubmission.submittedAt || lastSubmission.resubmittedAt, lastSubmission.month || 'Sin fecha') : 'Sin envíos' },
    { label: 'Nivel actual', value: level },
  ];

  return (
    <div className="p-4 md:p-8 bg-[#F6F8FB] min-h-screen space-y-6">
      <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-6">
        <div className="rounded-xl bg-white border border-slate-200/70 p-6 shadow-sm space-y-6 md:p-8">
          <div className="space-y-2">
            <p className="text-[0.6rem] uppercase text-slate-400">Trayectoria</p>
            <h1 className="text-3xl font-bold text-[#004AAD]">Tu aporte al Barómetro</h1>
            <p className="text-slate-600">
              Cada estrella representa un cuestionario enviado y validado. Mientras más constancia tengas, más peso gana tu sector.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 py-4">
            {[...Array(12)].map((_, i) => (
              <StarIcon
                key={i}
                solid={i < stars}
                className={`h-10 w-10 ${i < stars ? 'text-amber-400' : 'text-slate-200'}`}
              />
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-center">
                <p className="text-[0.6rem] uppercase text-slate-400">{card.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-2">{card.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-[#004AAD]/20 bg-[#EFF5FF] p-6 shadow-sm space-y-4 md:p-8">
          <p className="text-[0.6rem] uppercase text-[#004AAD]/60">Meta anual</p>
          <p className="text-3xl font-bold text-[#004AAD]">{Math.min(stars, 12)}/12 envíos</p>
          <p className="text-[#004AAD]/80">
            Cuando completes 12 participaciones durante el año, destacaremos a tu empresa en el boletín sectorial.
          </p>
          <div className="rounded-xl bg-white border border-[#004AAD]/10 p-4 space-y-2">
            <p className="text-[0.6rem] uppercase text-slate-400">Siguiente hito</p>
            <p className="text-base font-semibold text-slate-900">
              {stars >= 12 ? 'Meta anual alcanzada' : `Envía ${12 - stars} cuestionario(s) más para completarla`}
            </p>
          </div>
        </div>
      </div>

      {responseDigest && (
        <div className="grid gap-6 lg:grid-cols-[1.4fr_0.6fr]">
          <div className="rounded-xl bg-white border border-slate-200/70 p-6 shadow-sm space-y-6 md:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[0.6rem] uppercase text-slate-400">Pulso histórico</p>
                <h3 className="text-2xl font-semibold text-slate-900">Pilares con mejor percepción</h3>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Respuestas favorables</p>
                <p className="text-3xl font-bold text-emerald-600">{Math.round(responseDigest.favorableRatio * 100)}%</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {responseDigest.dimensionAverages.slice(0, 3).map((dimension) => (
                <div key={dimension.name} className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-2">
                  <p className="text-[0.6rem] uppercase text-slate-400">{dimension.name}</p>
                  <p className="text-2xl font-semibold text-slate-900">{dimension.score.toFixed(1)}/5</p>
                  <div className="h-2 rounded-full bg-slate-200">
                    <div
                      className="h-2 rounded-full bg-[#004AAD]"
                      style={{ width: `${Math.min(100, (dimension.score / 5) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500">
              Calculado con tus respuestas tipo Likert. Consideramos favorables las valoraciones de 4 (Buena) y 5 (Excelente).
            </p>
          </div>
          <div className="rounded-xl bg-white border border-slate-200/70 p-6 shadow-sm space-y-4 md:p-8">
            <p className="text-[0.6rem] uppercase text-slate-400">Dolores recurrentes</p>
            <h3 className="text-2xl font-semibold text-slate-900">Temas con menor calificación</h3>
            <div className="space-y-3">
              {responseDigest.weakQuestions.map((item) => (
                <div key={item.text} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-[0.6rem] uppercase text-slate-400">{item.dimension}</p>
                  <p className="text-sm font-semibold text-slate-900">{item.text}</p>
                  <p className="text-xs text-slate-500 mt-1">Promedio histórico: {item.score.toFixed(1)}/5</p>
                </div>
              ))}
              {responseDigest.weakQuestions.length === 0 && (
                <p className="text-sm text-slate-500">Aún no hay suficiente información para detectar patrones.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyRewards;
